/**
 * Redux Store Configuration
 * Central store for application state management
 */

import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import authReducer from './slices/authSlice';
import signupReducer from './slices/signupSlice';
import onboardingReducer from './slices/onboardingSlice';
import uiReducer from './slices/uiSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import enabledModulesReducer from './slices/enabledModulesSlice';

// Entity slices
import riskReducer from '../entities/risk/model/riskSlice';
import controlReducer from '../entities/control/model/controlSlice';
import complianceReducer from '../entities/compliance/model/complianceSlice';
import auditReducer from '../entities/audit/model/auditSlice';

// RTK Query APIs
import { riskApi } from '../entities/risk/api/riskApi';
import { controlApi } from '../entities/control/api/controlApi';
import { complianceApi } from '../entities/compliance/api/complianceApi';
import { auditApi } from '../entities/audit/api/auditApi';
import { kriApi } from '../entities/kri/api/kriApi';
import { systemConfigApi } from '../shared/api/systemConfigApi';
import { archiveApi } from '../shared/api/archiveApi';
import { orgApi } from '../entities/org/api/orgApi';
import { approvalsApi } from '../entities/approvals/api/approvalsApi';
import { authApi } from '../entities/auth/api/authApi';
import { governanceApi } from '../entities/governance/api/governanceApi';
import { ethicsApi } from '../entities/ethics/api/ethicsApi';
import { techManagementApi } from '../entities/tech-management/api/techManagementApi';
import { integrationApi } from '../entities/integration/api/integrationApi';
import { moduleApi } from '../entities/identity/api/moduleApi';
import { identityAdminApi } from '../entities/identity/api/adminApi';
import { auditTrailApi } from '../shared/api/auditTrailApi';
import { notificationApi } from '../shared/api/notificationApi';
import { masterDataApi } from '../entities/master-data/api/masterDataApi';
import { aiGovernanceApi } from '../entities/ai-governance/api/aiGovernanceApi';
import { platformOpsApi } from '../shared/api/platformOpsApi';
import { aiApi } from '../entities/ai/api/aiApi';
import { deadlinesApi } from '../entities/deadlines/api/deadlinesApi';
import { regchangeApi } from '../entities/regchange/api/regchangeApi';
import { evidenceRequestsApi } from '../entities/evidence-requests/api/evidenceRequestsApi';

export const store = configureStore({
  reducer: {
    auth: authReducer,
    signup: signupReducer,
    onboarding: onboardingReducer,
    ui: uiReducer,
    subscription: subscriptionReducer,
    enabledModules: enabledModulesReducer,

    // Entity slices
    risk: riskReducer,
    control: controlReducer,
    compliance: complianceReducer,
    audit: auditReducer,

    // RTK Query API slices
    [riskApi.reducerPath]: riskApi.reducer,
    [controlApi.reducerPath]: controlApi.reducer,
    [complianceApi.reducerPath]: complianceApi.reducer,
    [auditApi.reducerPath]: auditApi.reducer,
    [kriApi.reducerPath]: kriApi.reducer,
    [systemConfigApi.reducerPath]: systemConfigApi.reducer,
    [archiveApi.reducerPath]: archiveApi.reducer,
    [orgApi.reducerPath]: orgApi.reducer,
    [approvalsApi.reducerPath]: approvalsApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [governanceApi.reducerPath]: governanceApi.reducer,
    [ethicsApi.reducerPath]: ethicsApi.reducer,
    [techManagementApi.reducerPath]: techManagementApi.reducer,
    [integrationApi.reducerPath]: integrationApi.reducer,
    [moduleApi.reducerPath]: moduleApi.reducer,
    [identityAdminApi.reducerPath]: identityAdminApi.reducer,
    [auditTrailApi.reducerPath]: auditTrailApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [masterDataApi.reducerPath]: masterDataApi.reducer,
    [aiGovernanceApi.reducerPath]: aiGovernanceApi.reducer,
    [platformOpsApi.reducerPath]: platformOpsApi.reducer,
    [aiApi.reducerPath]: aiApi.reducer,
    [regchangeApi.reducerPath]: regchangeApi.reducer,
    [deadlinesApi.reducerPath]: deadlinesApi.reducer,
    [evidenceRequestsApi.reducerPath]: evidenceRequestsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types and paths for serialization check
        ignoredActions: [
          'persist/PERSIST',
          'riskApi/executeQuery/fulfilled',
          'controlApi/executeQuery/fulfilled',
          'complianceApi/executeQuery/fulfilled',
          'auditApi/executeQuery/fulfilled',
          'kriApi/executeQuery/fulfilled',
          'systemConfigApi/executeQuery/fulfilled',
          'orgApi/executeQuery/fulfilled',
          'approvalsApi/executeQuery/fulfilled',
          'authApi/executeQuery/fulfilled',
          'governanceApi/executeQuery/fulfilled',
          'ethicsApi/executeQuery/fulfilled',
          'techManagementApi/executeQuery/fulfilled',
          'integrationApi/executeQuery/fulfilled',
          'moduleApi/executeQuery/fulfilled',
          'identityAdminApi/executeQuery/fulfilled',
          'auditTrailApi/executeQuery/fulfilled',
          'notificationApi/executeQuery/fulfilled',
          'masterDataApi/executeQuery/fulfilled',
          'aiGovernanceApi/executeQuery/fulfilled',
          'platformOpsApi/executeQuery/fulfilled',
          'aiApi/executeQuery/fulfilled',
          'regchangeApi/executeQuery/fulfilled',
          'deadlinesApi/executeQuery/fulfilled',
          'evidenceRequestsApi/executeQuery/fulfilled',
        ],
        ignoredPaths: [
          'riskApi',
          'controlApi',
          'complianceApi',
          'auditApi',
          'kriApi',
          'systemConfigApi',
          'archiveApi',
          'orgApi',
          'approvalsApi',
          'authApi',
          'governanceApi',
          'ethicsApi',
          'techManagementApi',
          'integrationApi',
          'moduleApi',
          'identityAdminApi',
          'auditTrailApi',
          'notificationApi',
          'masterDataApi',
          'aiGovernanceApi',
          'platformOpsApi',
          'aiApi',
          'regchangeApi',
          'deadlinesApi',
          'evidenceRequestsApi',
        ],
      },
    })
      .concat(riskApi.middleware)
      .concat(controlApi.middleware)
      .concat(complianceApi.middleware)
      .concat(auditApi.middleware)
      .concat(kriApi.middleware)
      .concat(systemConfigApi.middleware)
      .concat(archiveApi.middleware)
      .concat(orgApi.middleware)
      .concat(approvalsApi.middleware)
      .concat(authApi.middleware)
      .concat(governanceApi.middleware)
      .concat(ethicsApi.middleware)
      .concat(techManagementApi.middleware)
      .concat(integrationApi.middleware)
      .concat(moduleApi.middleware)
      .concat(identityAdminApi.middleware)
      .concat(auditTrailApi.middleware)
      .concat(notificationApi.middleware)
      .concat(masterDataApi.middleware)
      .concat(aiGovernanceApi.middleware)
      .concat(platformOpsApi.middleware)
      .concat(aiApi.middleware)
      .concat(regchangeApi.middleware)
      .concat(deadlinesApi.middleware)
      .concat(evidenceRequestsApi.middleware),
  devTools: import.meta.env.MODE !== 'production',
});

// Enable refetchOnFocus/refetchOnReconnect behaviors
setupListeners(store.dispatch);

// Type inference helpers (for JSDoc or TypeScript)
// Use these in your components:
// @type {ReturnType<typeof store.getState>} for RootState
// @type {typeof store.dispatch} for AppDispatch

export default store;
